import React, {useContext} from "react";
import {NmiPayments} from "@nmipayments/nmi-pay-react";
import type {Appearance, PaymentChangeEvent, PaymentMethod} from "@nmipayments/nmi-pay-react";
import {CompanyContext} from "../../../../context/CompanyContext";
import {AmountInput} from "../AmountInput/AmountInput";
import {PAYMENT_METHOD_TYPE} from "../../inputs/AchSwitcher/AchSwitcher";
import "./NmiForm.scss";

interface NmiFormProps {
    tokenizationKey: string;
    amount: string;
    setAmount: (amount: string) => void;
    setIsAmountValid: (isValid: boolean) => void;
    onApprovedPayment: (token: string, paymentMethod: PAYMENT_METHOD_TYPE) => void;
    onSetValid: (isValid: boolean) => void;
    onFieldsAvailable: () => void;
    isCreditCardAvailable: boolean;
    isAchAvailable: boolean;
}

const NMI_COLOR_PRIMARY = '#00034D';
const NMI_COLOR_DANGER = '#d93c37';

/**
 * NMI `@nmipayments/nmi-core` merges `customTheme` into the base light/dark theme (deep merge).
 * Runtime accepts partial `colors`; TS types `Partial<NmiTheme>` shallowly, so we assert to `Appearance`.
 */
const NMI_APPEARANCE: Appearance = {
    theme: 'light',
    layoutSpacing: 'compact',
    customTheme: {
        colors: {
            primary: {
                default: NMI_COLOR_PRIMARY,
                foreground: '#ffffff',
                ['100']: '#cceaf7',
                ['200']: '#66bfe6'
            },
            danger: {
                default: NMI_COLOR_DANGER,
                foreground: '#ffffff',
                ['100']: '#fdeded',
                ['200']: '#f49691'
            }
        }
    } as Appearance['customTheme']
};

export const NmiForm: React.FunctionComponent<NmiFormProps> = (props) => {
    const company = useContext(CompanyContext);

    const paymentMethods: PaymentMethod[] = [
        ...(props.isCreditCardAvailable ? (['card'] as PaymentMethod[]) : []),
        ...(props.isAchAvailable ? (['ach'] as PaymentMethod[]) : []),
    ];

    const onChange = (data: PaymentChangeEvent) => {
        props.onSetValid(data.complete);
        if (data.complete && data.token) {
            const lookupData = data.lookupData as
                | {check?: {account?: string | null}}
                | undefined;
            const paymentType: PaymentMethod = lookupData?.check?.account
                ? 'ach'
                : 'card';
            props.onApprovedPayment(
                data.token,
                paymentType === 'ach' ? PAYMENT_METHOD_TYPE.ACH : PAYMENT_METHOD_TYPE.CREDIT_CARD
            );
        }
    };

    if (!props.tokenizationKey) {
        return null;
    }

    return (
        <div className='nmi-form'>
            <div className='form-group'>
                <div className='input-container nmi-form__amount-input-container'>
                    <label>Amount ({company.currency_code}):</label>
                    <AmountInput
                        amount={props.amount}
                        maxAmount={props.amount}
                        onSetAmount={props.setAmount}
                        onSetIsAmountValid={props.setIsAmountValid}
                    />
                </div>
            </div>
            <NmiPayments
                tokenizationKey={props.tokenizationKey}
                paymentMethods={paymentMethods}
                appearance={NMI_APPEARANCE}
                onFieldsAvailable={props.onFieldsAvailable}
                onChange={onChange}
            />
        </div>
    );
};
